import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchUsers } from '@/features/users/api'
import LoadingState from '@/components/common/LoadingState'
import EmptyState from '@/components/common/EmptyState'
import ErrorState from '@/components/common/ErrorState'

const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin',
  staff: 'Staff',
  client: 'Client',
  other: 'Other',
}

/** Ports includes/users.html — the user directory, grouped by role in the
 * legacy template via three separate Jinja loops; here it's one list with a
 * role filter, same underlying grouping. */
export default function UsersPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
  })
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [search, setSearch] = useState('')

  const filtered = (data ?? []).filter((u) => {
    if (roleFilter !== 'all' && u.role_type !== roleFilter) return false
    if (!search) return true
    const q = search.toLowerCase()
    return (
      u.email.toLowerCase().includes(q) ||
      (u.firstname ?? '').toLowerCase().includes(q) ||
      (u.lastname ?? '').toLowerCase().includes(q)
    )
  })

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text-primary">Users</h1>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email…"
          className="rounded border border-border bg-input px-3 py-1.5 text-sm text-text-primary"
        />
        {['all', 'admin', 'staff', 'client', 'other'].map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRoleFilter(r)}
            className={`rounded px-3 py-1.5 text-sm ${
              roleFilter === r ? 'bg-brand text-white' : 'border border-border text-text-primary hover:bg-bg-secondary'
            }`}
          >
            {r === 'all' ? 'All' : ROLE_LABEL[r]}
          </button>
        ))}
      </div>

      {isLoading && <LoadingState label="Loading users…" />}
      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : 'Failed to load users.'}
          onRetry={() => refetch()}
        />
      )}
      {data && filtered.length === 0 && <EmptyState message="No users match." />}

      {filtered.length > 0 && (
        <div className="overflow-x-auto rounded border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-bg-secondary text-left text-text-primary">
                <th className="px-4 py-2 font-semibold">Name</th>
                <th className="px-4 py-2 font-semibold">Email</th>
                <th className="px-4 py-2 font-semibold">Company</th>
                <th className="px-4 py-2 font-semibold">Role</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.user_id} className="border-b border-border last:border-0 hover:bg-bg-secondary">
                  <td className="px-4 py-2">
                    <Link to={`/users/${u.user_id}`} className="text-accent hover:underline">
                      {u.firstname} {u.lastname}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-text-primary">{u.email}</td>
                  <td className="px-4 py-2 text-text-primary">{u.company_name ?? '—'}</td>
                  <td className="px-4 py-2 text-text-primary">{ROLE_LABEL[u.role_type]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
